import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { SUBSCRIPTION_LIMITS, SubscriptionTier } from '@/types/subscription';
import { UpgradeModal } from '@/components/UpgradeModal';

interface Subscription {
  id: string;
  user_id: string;
  tier: SubscriptionTier;
  status: 'active' | 'cancelled' | 'expired';
  trial_end?: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  payment_method?: string;
  paypal_subscription_id?: string;
  premium_end_date?: string;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  activeProductCount: number;
  loading: boolean;
  error: Error | null;
  canAddProduct: boolean;
  showUpgradeModal: () => void;
  isUpgradeModalOpen: boolean;
  closeUpgradeModal: () => void;
  fetchSubscription: () => Promise<void>;
  startTrial: () => Promise<void>;
  isTrialAvailable: boolean;
  daysLeftInTrial: number | null;
  maxActiveProducts: number;
  hasFeature: (featureId: string) => boolean;
  updateActiveProductCount: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [activeProductCount, setActiveProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscription();
      fetchActiveProductCount();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      setSubscription(data);
    } catch (err) {
      console.error('Fehler beim Laden des Abonnements:', err);
      setError(err instanceof Error ? err : new Error('Fehler beim Laden des Abonnements'));
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProductCount = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*')
        .eq('user_id', user?.id)
        .eq('status', 'waiting');

      if (error) throw error;
      setActiveProductCount(data?.length || 0);
    } catch (err) {
      console.error('Fehler beim Laden der aktiven Produkte:', err);
    }
  };

  // Funktion zum manuellen Aktualisieren des Produktzählers
  const updateActiveProductCount = () => {
    if (user) {
      fetchActiveProductCount();
    }
  };

  const getCurrentTier = (): SubscriptionTier => {
    if (!subscription) return 'free';
    return subscription.tier;
  };

  const maxActiveProducts = SUBSCRIPTION_LIMITS[getCurrentTier()].maxActiveProducts;

  const canAddProduct = !loading && (
    activeProductCount < maxActiveProducts
  );

  const isTrialAvailable = subscription?.tier === 'free' && !subscription?.trial_used;

  const daysLeftInTrial = subscription?.trial_end 
    ? Math.max(0, Math.ceil((new Date(subscription.trial_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : null;

  const hasFeature = (featureId: string): boolean => {
    if (!subscription) return false;
    const currentTier = getCurrentTier();
    const tierLimits = SUBSCRIPTION_LIMITS[currentTier];
    return tierLimits.features.includes(featureId);
  };

  const startTrial = async () => {
    try {
      if (!user?.id || !isTrialAvailable) return;

      const { error } = await supabase
        .from('subscriptions')
        .update({
          tier: 'trial',
          status: 'active',
          trial_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          trial_used: true,
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          cancel_at_period_end: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchSubscription();
    } catch (err) {
      console.error('Fehler beim Starten der Testphase:', err);
      throw err;
    }
  };

  const showUpgradeModal = () => setIsUpgradeModalOpen(true);
  const closeUpgradeModal = () => setIsUpgradeModalOpen(false);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      activeProductCount, 
      loading, 
      error,
      canAddProduct,
      showUpgradeModal,
      isUpgradeModalOpen,
      closeUpgradeModal,
      fetchSubscription,
      startTrial,
      isTrialAvailable,
      daysLeftInTrial,
      maxActiveProducts,
      hasFeature,
      updateActiveProductCount
    }}>
      {children}
      <UpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={closeUpgradeModal}
      />
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription muss innerhalb eines SubscriptionProviders verwendet werden');
  }
  return context;
} 